import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';

const API_URL = process.env.GATSBY_CHATBOT_API_URL || 'http://localhost:8001/api';

// ---------------------------------------------------------------------------
// Session helper — generate once, persist in localStorage
// ---------------------------------------------------------------------------
const getSessionId = () => {
  if (typeof window === 'undefined') return '';
  const KEY = 'chatbot_session_id';
  let id = localStorage.getItem(KEY);
  if (!id) {
    id =
      'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });
    localStorage.setItem(KEY, id);
  }
  return id;
};

// ---------------------------------------------------------------------------
// Animations
// ---------------------------------------------------------------------------
const fadeSlideUp = keyframes`
  from { opacity: 0; transform: translateY(16px) scale(0.96); }
  to   { opacity: 1; transform: translateY(0)   scale(1);     }
`;

const pulse = keyframes`
  0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
  40%           { opacity: 1;   transform: scale(1);    }
`;

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------
const Overlay = styled.div`
  display: none;
  @media (max-width: 480px) {
    display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 99;
  }
`;

const Wrapper = styled.div`
  position: fixed;
  bottom: 40px;
  right: 40px;
  z-index: 100;
  font-family: var(--font-sans);

  @media (max-width: 480px) {
    bottom: 20px;
    right: 20px;
  }
`;

const ToggleButton = styled.button`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: none;
  background: var(--green);
  color: var(--dark-navy);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(100, 255, 218, 0.25);
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  pointer-events: ${({ visible }) => (visible ? 'auto' : 'none')};
  transition: opacity 0.5s ease-in-out, transform 0.3s var(--easing), box-shadow 0.3s var(--easing);

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 36px rgba(100, 255, 218, 0.45);
  }

  svg {
    width: 26px;
    height: 26px;
    fill: currentColor;
    transition: transform 0.25s var(--easing);
  }
`;

const greetingSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(12px) translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateX(0) translateY(0);
  }
`;

const GreetingBubble = styled.div`
  position: absolute;
  bottom: 68px;
  right: 0;
  width: 280px;
  padding: 14px 16px;
  background: var(--light-navy);
  color: var(--light-slate);
  font-size: var(--fz-xs);
  line-height: 1.55;
  border-radius: 12px;
  border-bottom-right-radius: 4px;
  box-shadow: 0 8px 30px rgba(2, 6, 12, 0.5);
  animation: ${greetingSlideIn} 0.4s var(--easing) forwards;
  opacity: ${({ fading }) => (fading ? 0 : 1)};
  transition: opacity 0.35s ease-out;
  pointer-events: none;

  &::after {
    content: '';
    position: absolute;
    bottom: -6px;
    right: 20px;
    width: 12px;
    height: 12px;
    background: var(--light-navy);
    transform: rotate(45deg);
    border-radius: 2px;
  }

  @media (max-width: 480px) {
    width: 240px;
  }
`;

const Window = styled.div`
  position: absolute;
  bottom: 72px;
  right: 0;
  width: 380px;
  max-height: 520px;
  background: var(--navy);
  border: 1px solid var(--light-navy);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(2, 6, 12, 0.6);
  animation: ${fadeSlideUp} 0.25s var(--easing) forwards;

  @media (max-width: 480px) {
    position: fixed;
    bottom: 0;
    right: 0;
    left: 0;
    width: 100%;
    max-height: 85vh;
    border-radius: 16px 16px 0 0;
    animation: none;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  background: var(--dark-navy);
  border-bottom: 1px solid var(--light-navy);
  flex-shrink: 0;

  .header-info {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: var(--green);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    color: var(--dark-navy);
    font-weight: 700;
  }

  .title {
    color: var(--lightest-slate);
    font-size: var(--fz-sm);
    font-weight: 600;
    line-height: 1.2;
  }

  .subtitle {
    color: var(--dark-slate);
    font-size: var(--fz-xxs);
    line-height: 1.2;
  }

  .close-btn {
    background: none;
    border: none;
    color: var(--slate);
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    transition: color 0.2s;

    &:hover {
      color: var(--lightest-slate);
    }

    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

const Messages = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  scroll-behavior: smooth;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--light-navy);
    border-radius: 4px;
  }
`;

const bubbleBase = css`
  max-width: 85%;
  padding: 10px 14px;
  border-radius: 12px;
  font-size: var(--fz-sm);
  line-height: 1.55;
  word-wrap: break-word;
  white-space: pre-wrap;
`;

const BotBubble = styled.div`
  ${bubbleBase};
  align-self: flex-start;
  background: var(--light-navy);
  color: var(--light-slate);
  border-bottom-left-radius: 4px;
`;

const UserBubble = styled.div`
  ${bubbleBase};
  align-self: flex-end;
  background: var(--green);
  color: var(--dark-navy);
  border-bottom-right-radius: 4px;
  font-weight: 500;
`;

const ErrorBubble = styled.div`
  ${bubbleBase};
  align-self: flex-start;
  background: rgba(239, 68, 68, 0.12);
  color: #fca5a5;
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-bottom-left-radius: 4px;
  font-size: var(--fz-xs);
`;

const TypingDots = styled.div`
  align-self: flex-start;
  display: flex;
  gap: 5px;
  padding: 12px 16px;
  background: var(--light-navy);
  border-radius: 12px;
  border-bottom-left-radius: 4px;

  span {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--slate);
    animation: ${pulse} 1.2s infinite;

    &:nth-child(2) { animation-delay: 0.15s; }
    &:nth-child(3) { animation-delay: 0.3s;  }
  }
`;

const InputArea = styled.form`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 12px 14px;
  border-top: 1px solid var(--light-navy);
  background: var(--dark-navy);
  flex-shrink: 0;
`;

const TextInput = styled.textarea`
  flex: 1;
  resize: none;
  border: 1px solid var(--light-navy);
  border-radius: 8px;
  background: var(--navy);
  color: var(--lightest-slate);
  font-family: var(--font-sans);
  font-size: var(--fz-sm);
  padding: 10px 12px;
  line-height: 1.4;
  max-height: 100px;
  outline: none;
  transition: border-color 0.2s;

  &::placeholder {
    color: var(--dark-slate);
  }

  &:focus {
    border-color: var(--green);
  }
`;

const SendButton = styled.button`
  width: 38px;
  height: 38px;
  border-radius: 8px;
  border: none;
  background: ${({ disabled }) => (disabled ? 'var(--light-navy)' : 'var(--green)')};
  color: var(--dark-navy);
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.2s, transform 0.15s;

  &:not(:disabled):hover {
    transform: scale(1.06);
  }

  svg {
    width: 18px;
    height: 18px;
    fill: currentColor;
  }
`;

const CharCount = styled.div`
  font-size: 11px;
  color: ${({ isOver }) => (isOver ? '#fca5a5' : 'var(--dark-slate)')};
  text-align: right;
  padding: 0 16px 4px;
  background: var(--dark-navy);
`;

const WelcomeMessage = styled.div`
  text-align: center;
  padding: 20px 16px;
  color: var(--dark-slate);
  font-size: var(--fz-xs);
  line-height: 1.5;

  .welcome-title {
    color: var(--lightest-slate);
    font-size: var(--fz-md);
    font-weight: 600;
    margin-bottom: 6px;
  }
`;

// ---------------------------------------------------------------------------
// Icons (inline SVG — no extra deps)
// ---------------------------------------------------------------------------
const ChatIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.2L4 17.2V4h16v12z" />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const SendIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
  </svg>
);

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const MAX_CHARS = 500;
const WELCOME_TEXT = "Hi! I'm Biruk's portfolio assistant. Ask me about his projects, skills, or experience.";

const SCROLL_DISMISS_PX = 400;

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [iconVisible, setIconVisible] = useState(false);
  const [showGreeting, setShowGreeting] = useState(false);
  const [greetingFading, setGreetingFading] = useState(false);
  const greetingDismissedRef = useRef(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  // --- Entrance: icon fades in at 1.5s, greeting appears at 4s ---
  useEffect(() => {
    const iconTimer = setTimeout(() => setIconVisible(true), 1500);
    const greetTimer = setTimeout(() => {
      if (!greetingDismissedRef.current) setShowGreeting(true);
    }, 4000);
    return () => {
      clearTimeout(iconTimer);
      clearTimeout(greetTimer);
    };
  }, []);

  // --- Scroll listener: dismiss greeting past 400px ---
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > SCROLL_DISMISS_PX && showGreeting && !isOpen) {
        setGreetingFading(true);
        setTimeout(() => {
          setShowGreeting(false);
          setGreetingFading(false);
          greetingDismissedRef.current = true;
        }, 350);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [showGreeting, isOpen]);

  // Auto-scroll to bottom
  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading, scrollToBottom]);

  // Focus textarea when opened
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      setTimeout(() => textareaRef.current.focus(), 100);
    }
  }, [isOpen]);

  // Auto-resize textarea + clear stale errors on new input
  const handleInputChange = e => {
    const val = e.target.value;
    if (val.length <= MAX_CHARS) {
      setInput(val);
    }
    // Clear the most recent error when user starts typing again
    setMessages(prev => {
      if (prev.length > 0 && prev[prev.length - 1].role === 'error') {
        return prev.slice(0, -1);
      }
      return prev;
    });
    // Auto-resize
    const ta = textareaRef.current;
    if (ta) {
      ta.style.height = 'auto';
      ta.style.height = Math.min(ta.scrollHeight, 100) + 'px';
    }
  };

  const sendMessage = async e => {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed || trimmed.length < 2 || loading) return;

    const userMsg = { role: 'user', content: trimmed };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    try {
      console.log('\u{1F680} Request sent to backend at:', new Date().toISOString());
      const resp = await fetch(`${API_URL}/chat/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Id': getSessionId(),
        },
        body: JSON.stringify({ message: trimmed }),
      });

      if (resp.status === 429) {
        setMessages(prev => [
          ...prev,
          { role: 'error', content: "You're sending messages too quickly. Please wait a moment and try again." },
        ]);
        return;
      }

      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.error || `Server error (${resp.status})`);
      }

      const data = await resp.json();
      setMessages(prev => [...prev, { role: 'bot', content: data.reply }]);
    } catch (err) {
      setMessages(prev => [
        ...prev,
        { role: 'error', content: err.message || 'Something went wrong. Please try again.' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(e);
    }
  };

  const toggle = () => {
    setIsOpen(prev => {
      if (!prev) {
        setShowGreeting(false);
        greetingDismissedRef.current = true;
      }
      return !prev;
    });
  };

  return (
    <>
      <Overlay isOpen={isOpen} onClick={toggle} />
      <Wrapper>
        {isOpen && (
          <Window>
            <Header>
              <div className="header-info">
                <div className="avatar">BK</div>
                <div>
                  <div className="title">Biruk's Assistant</div>
                  <div className="subtitle">Ask me anything</div>
                </div>
              </div>
              <button className="close-btn" onClick={toggle} aria-label="Close chat">
                <CloseIcon />
              </button>
            </Header>

            <Messages>
              <WelcomeMessage>
                <div className="welcome-title">👋 Welcome</div>
                {WELCOME_TEXT}
              </WelcomeMessage>

              {messages.map((msg, i) => {
                if (msg.role === 'user') return <UserBubble key={i}>{msg.content}</UserBubble>;
                if (msg.role === 'error') return <ErrorBubble key={i}>{msg.content}</ErrorBubble>;
                return <BotBubble key={i}>{msg.content}</BotBubble>;
              })}

              {loading && (
                <TypingDots>
                  <span /><span /><span />
                </TypingDots>
              )}

              <div ref={messagesEndRef} />
            </Messages>

            {input.length > 0 && (
              <CharCount isOver={input.length >= MAX_CHARS}>
                {input.length}/{MAX_CHARS}
              </CharCount>
            )}

            <InputArea onSubmit={sendMessage}>
              <TextInput
                ref={textareaRef}
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="Ask about Biruk's projects…"
                rows={1}
                maxLength={MAX_CHARS}
                disabled={loading}
              />
              <SendButton type="submit" disabled={!input.trim() || loading} aria-label="Send message">
                <SendIcon />
              </SendButton>
            </InputArea>
          </Window>
        )}

        {showGreeting && !isOpen && (
          <GreetingBubble fading={greetingFading}>
            Hi! I'm Biruk's AI assistant. Ask me about his projects, skills, or experience.
          </GreetingBubble>
        )}

        <ToggleButton
          onClick={toggle}
          visible={iconVisible || isOpen}
          aria-label={isOpen ? 'Close chat' : 'Open chat'}
        >
          {isOpen ? <CloseIcon /> : <ChatIcon />}
        </ToggleButton>
      </Wrapper>
    </>
  );
};

export default ChatWidget;
